/**
 * 🟢 ACTIVE COMPONENT - Main customer-facing navigation header
 * Used in: GuestLayout, CustomerLayout
 * See: NAVIGATION_COMPONENTS.md for full documentation
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Menu, X, ShoppingCart, ChevronDown, User, LogOut, Package, Heart, BarChart3, TrendingUp, Zap, ShieldCheck, Monitor, Smartphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUI } from '@/contexts/UIContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { ORDER_SITE_URL } from '@/lib/externalLinks';

const CelebrationNavHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, userRoles } = useAuth();
  const { isDesktopView, toggleDesktopView } = useUI();

  // Fetch customer profile for avatar
  const { data: customer, isLoading: isProfileLoading } = useQuery({
    queryKey: ['customer-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Profile fetch error:', error);
          return null;
        }
        return data;
      } catch (err) {
        console.error('Profile fetch exception:', err);
        return null;
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  // Check if user can be admin (same logic as AuthContext)
  const canBeAdmin = userRoles?.some(r =>
    ['admin', 'super_admin', 'branch_admin', 'staff', 'branch_staff', 'rider', 'kitchen', 'customer_service', 'dispatcher'].includes(r.role)
  ) ?? false;

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = [
    { 
      label: 'Menu', 
      path: '/menu' 
    },
    { 
      label: 'Blog', 
      path: '/blog' 
    },
    { 
      label: 'About', 
      path: '/about' 
    }
  ];

  const mobileNavItems = [
    { label: 'Home', path: '/' },
    { label: 'Menu', path: '/menu' },
    { label: 'Blog Stories', path: '/blog' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Rewards Program', path: '/rewards' },
    { label: 'WhatsApp Ordering', path: '/whatsapp-ordering' },
    { label: 'Delivery Zones', path: '/delivery-zones' },
    { label: 'Reviews', path: '/reviews' },
    { label: 'About Us', path: '/about' },
    { label: 'Contact', path: '/contact' }
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Main Navigation Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white shadow-lg' 
            : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-2 group"
            >
              <img src="/images/dmf_logo_optimized.webp" alt="Dough Man Foods" className="h-8 w-8 sm:h-10 sm:w-10"
                   onError={(e) => { e.currentTarget.src = "/images/dmf_logo.png"; }} />
              <span className="font-bold text-xl sm:text-2xl text-gray-900 group-hover:text-amber-600 transition-colors">
                Dough Man
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <div 
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-1 font-medium text-sm xl:text-base transition-colors ${
                      isActive(item.path)
                        ? 'text-amber-600'
                        : 'text-gray-700 hover:text-amber-600'
                    }`}
                  >
                    <span>{item.label}</span>
                    {item.dropdown && (
                      <ChevronDown className="w-3 h-3 transition-transform" 
                        style={{ transform: activeDropdown === item.label ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    )}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.dropdown && activeDropdown === item.label && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-1">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              <a
                href={ORDER_SITE_URL}
                className="hidden sm:flex items-center gap-2 rounded-full bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                <ShoppingCart className="w-4 h-4" />
                Order Online
              </a>

              {/* Authentication - Desktop */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="hidden sm:flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300"
                    >
                      {customer?.avatar_url ? (
                        <img 
                          src={customer.avatar_url} 
                          alt={customer?.first_name || 'User'}
                          className="h-5 w-5 rounded-full object-cover"
                          onError={(e) => {
                            console.error('Avatar image failed to load:', customer.avatar_url);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : null}
                      {!customer?.avatar_url && (
                        <User className="w-4 h-4" />
                      )}
                      {user?.user_metadata?.first_name || 'Account'}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex items-center gap-3">
                        {customer?.avatar_url ? (
                          <img 
                            src={customer.avatar_url} 
                            alt={customer?.first_name || 'User'}
                            className="h-10 w-10 rounded-full object-cover border border-gray-200"
                            onError={(e) => {
                              console.error('Avatar dropdown image failed to load:', customer.avatar_url);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        {!customer?.avatar_url && (
                          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-amber-700" />
                          </div>
                        )}
                        <div className="flex flex-col space-y-1 flex-1">
                          <p className="text-sm font-medium leading-none">
                            {user?.user_metadata?.first_name || 'Customer'}
                          </p>
                          <p className="text-xs leading-none text-muted-foreground truncate">
                            {user?.email || user?.phone || ''}
                          </p>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href={ORDER_SITE_URL} className="flex items-center">
                        <Package className="w-4 h-4 mr-2" />
                        My Orders
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wishlist" className="flex items-center">
                        <Heart className="w-4 h-4 mr-2" />
                        Wishlist
                      </Link>
                    </DropdownMenuItem>
                    
                    {/* <DropdownMenuSeparator />
                    
                    <DropdownMenuItem asChild>
                      <Link to="/insights" className="flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        My Insights
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem asChild>
                      <Link to="/engagement" className="flex items-center">
                        <Zap className="w-4 h-4 mr-2" />
                        Engagement
                      </Link>
                    </DropdownMenuItem> */}

                    {canBeAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin/dashboard" className="flex items-center">
                            <ShieldCheck className="w-4 h-4 mr-2" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={toggleDesktopView}
                      className="cursor-pointer"
                    >
                      {isDesktopView ? (
                        <>
                          <Smartphone className="w-4 h-4 mr-2" />
                          Mobile View
                        </>
                      ) : (
                        <>
                          <Monitor className="w-4 h-4 mr-2" />
                          Desktop View
                        </>
                      )}
                    </DropdownMenuItem>

                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="text-red-600 focus:text-red-600 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}

              {/* CTA Button - Desktop (for authenticated users) */}
              {user && (
                <a href={ORDER_SITE_URL}>
                  <Button
                    className="hidden sm:flex bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full px-6 font-semibold"
                  >
                    Order Package
                  </Button>
                </a>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-700 hover:text-amber-600 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Drawer */}
      <div 
        className={`fixed top-0 right-0 w-full max-w-sm h-full bg-white z-50 lg:hidden transform transition-transform duration-300 ${
          isMobileMenuOpen 
            ? 'translate-x-0' 
            : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-bold text-xl text-gray-900">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-700 hover:text-amber-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            {mobileNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-6 py-3 font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-amber-600 bg-amber-50'
                    : 'text-gray-700 hover:text-amber-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Footer */}
          <div className="p-4 border-t space-y-3">
            <a
              href={ORDER_SITE_URL}
              className="flex items-center justify-center space-x-2 w-full py-3 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <ShoppingCart className="w-5 h-5" />
              <span>Order Online</span>
            </a>
            
            {/* Authentication - Mobile */}
            {user ? (
              <div className="space-y-2">
                <Link
                  to="/dashboard"
                  className="flex items-center justify-center space-x-2 w-full py-3 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="w-5 h-5" />
                  <span>My Dashboard</span>
                </Link>

                {canBeAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center justify-center space-x-2 w-full py-3 border border-amber-300 bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <ShieldCheck className="w-5 h-5" />
                    <span>Admin Dashboard</span>
                  </Link>
                )}

                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleSignOut();
                  }}
                  variant="outline"
                  className="w-full py-3 border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Sign Out
                </Button>

                {/* View Mode Toggle for Mobile */}
                <Button
                  onClick={() => {
                    toggleDesktopView();
                    setIsMobileMenuOpen(false);
                  }}
                  variant="ghost"
                  className="w-full py-3 mt-2 text-gray-500 flex items-center justify-center gap-2"
                >
                  {isDesktopView ? (
                    <>
                      <Smartphone className="w-4 h-4" />
                      Switch to Mobile View
                    </>
                  ) : (
                    <>
                      <Monitor className="w-4 h-4" />
                      Switch to Desktop View
                    </>
                  )}
                </Button>
              </div>
            ) : null}
            
            <a href={ORDER_SITE_URL}>
              <Button
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full py-3 font-semibold"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Order Package
              </Button>
            </a>
          </div>

          {/* Social Links */}
          <div className="p-4 border-t">
            <div className="flex justify-center space-x-4">
              <a href="#" className="text-gray-400 hover:text-amber-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-amber-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-amber-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-16 sm:h-20" />
    </>
  );
};

export default CelebrationNavHeader;
